"use client";
import AlgoliaProvider from "@components/algolia/AlgoliaProvider";
import type { IPageProductListingPage } from "@contentful-api/types/contentful";
import { TARGET_GROUP_FACET } from "@lib/constants/filtersConstants";
import type { RecommendationResult } from "@lib/algolia/types";
import { getAdditionalSizesFilters } from "@lib/utils/plp/plpShoeSizes";
import { useStore } from "@store/store";
import { type InitialFiltersType } from "@store/types";
import { SearchPageTemplate } from "@templates/SearchPage";
import { isEqual } from "lodash-es";
import { useLocale } from "next-intl";
import { useSearchParams } from "next/navigation";
import { useEffect, useMemo } from "react";

type SearchClientProps = {
    readonly page: IPageProductListingPage;
    readonly initialFiltersList: InitialFiltersType;
    readonly trendingRecommendations: RecommendationResult[];
};

export default function SearchClient({
    page,
    initialFiltersList,
    trendingRecommendations,
}: SearchClientProps) {
    const initialFilters = useStore((state) => state.data.products.initialFilters);
    const setInitialFilters = useStore((state) => state.setInitialFilters);

    const locale = useLocale();
    const searchParams = useSearchParams();
    const query = searchParams.get("query") || "";
    const facetAttributes = useMemo(() => {
        const filtersList = [
            ...(page?.fields?.filtersList ?? []),
            ...getAdditionalSizesFilters(page?.fields?.filtersList),
        ];

        return [
            TARGET_GROUP_FACET,
            ...filtersList.map((filter) => filter.fields.facetName).filter(Boolean),
        ];
    }, [page]);
    useEffect(() => {
        if (!isEqual(initialFilters, initialFiltersList)) {
            setInitialFilters(initialFiltersList);
        }
    }, [searchParams]);

    return (
        <AlgoliaProvider locale={locale} facetAttributes={facetAttributes}>
            <SearchPageTemplate
                page={page}
                query={query}
                locale={locale}
                trendingRecommendations={trendingRecommendations}
            />
        </AlgoliaProvider>
    );
}
